/**
 * Discover the **data rows** a previewed DataList exposes for manual
 * drag-reorder (data-row-ordering Tier 2), and decide whether the surface
 * supports manual ordering at all.
 *
 * Unlike {@link discoverLayoutTargets} (which scans the rendered preview DOM
 * for field / column / section markers), rows are *records*: their stable
 * business id lives in the data, not in a `data-testid`. So this reads the
 * previewed component's **props** — specifically a DataList `config` — to find
 * the inline `data[]` and the stable row id of each row.
 *
 * The engine restricts manual ordering to fully-loaded, stable-id row sets, so
 * this mirrors that constraint and reports *why* a surface is unsupported:
 *
 * - **Not a DataList** (no `config.dataSource`) → unsupported.
 * - **Server-paginated** (`dataSource.endpoint` set, no inline `data`) →
 *   unsupported, because a single fetched page can't be meaningfully
 *   hand-ordered.
 * - **No stable row id** (a row is missing its `rowIdField` value, or two rows
 *   share an id) → unsupported, because `reorderRows` targets a *stable*,
 *   unique id, never the array index.
 * - Otherwise → supported, with one {@link RowTarget} per row.
 *
 * Pure: no React, no DOM, no fetch. The single argument is the previewed
 * component's resolved props (or `undefined` before a capture exists).
 */

import { DEFAULT_ROW_ID_FIELD } from '@components/integrated/data-list/constants';

import type { RowTarget } from './layoutAuthoring';
import type { RowReorderSupport } from './LayoutAuthoringPanel';

/** Outcome of scanning a previewed component for reorderable rows. */
export interface RowDiscovery {
  /** Rows available to drag-reorder, in natural order. Empty when unsupported. */
  readonly rowTargets: RowTarget[];
  /** Whether the surface supports manual ordering (and the reason if not). */
  readonly support: RowReorderSupport;
}

/** Disabled-support outcome with the given reason and no rows. */
function unsupported(reason: string): RowDiscovery {
  return { rowTargets: [], support: { supported: false, reason } };
}

/**
 * Pick a short human-friendly label for a row: the first string/number cell
 * value that isn't the id field, else the id itself. Preview-only — never the
 * contract a rule targets.
 */
function rowLabel(
  row: Record<string, unknown>,
  idField: string,
  id: string,
): string {
  for (const [key, value] of Object.entries(row)) {
    if (key === idField) continue;
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (typeof value === 'number') return String(value);
  }
  return id;
}

/**
 * Scan a previewed component's props for reorderable data rows. See the module
 * doc for the support rules. Returns the rows plus a {@link RowReorderSupport}
 * the authoring panel uses to either show the draggable rows or the documented
 * disabled message.
 */
export function discoverRowTargets(
  props: Record<string, unknown> | undefined,
): RowDiscovery {
  if (props === undefined) {
    return unsupported(
      'No preview is available, so no rows could be detected for manual ordering.',
    );
  }

  const config = props.config;
  if (config === null || typeof config !== 'object') {
    return unsupported(
      'Manual row ordering applies to data lists; this component is not a list.',
    );
  }

  const dataSource = (config as Record<string, unknown>).dataSource;
  if (dataSource === null || typeof dataSource !== 'object') {
    return unsupported(
      'Manual row ordering applies to data lists; this component is not a list.',
    );
  }

  const source = dataSource as Record<string, unknown>;

  // Server-paginated / streamed: a single fetched page can't be hand-ordered.
  if (source.endpoint !== undefined && source.data === undefined) {
    return unsupported(
      'Manual row ordering is unavailable for server-paginated data; order rows by a column sort instead.',
    );
  }

  const data = source.data;
  if (!Array.isArray(data)) {
    return unsupported(
      'This list has no fully-loaded inline data, so its rows cannot be manually ordered.',
    );
  }

  if (data.length === 0) {
    // A list with no rows is "supported" but has nothing to drag — the panel
    // renders nothing for an empty supported set.
    return { rowTargets: [], support: { supported: true } };
  }

  const idField =
    typeof source.rowIdField === 'string' && source.rowIdField !== ''
      ? source.rowIdField
      : DEFAULT_ROW_ID_FIELD;

  const rowTargets: RowTarget[] = [];
  const seen = new Set<string>();

  for (const raw of data) {
    if (raw === null || typeof raw !== 'object') {
      return unsupported(
        `Manual row ordering needs every row to carry a stable "${idField}" id; a row is not an object.`,
      );
    }
    const row = raw as Record<string, unknown>;
    const rawId = row[idField];
    // A stable id is the ERP/business key — a string or number, never an
    // object/array (and never the array index). Anything else is "missing".
    if (
      (typeof rawId !== 'string' && typeof rawId !== 'number') ||
      rawId === ''
    ) {
      return unsupported(
        `Manual row ordering needs every row to carry a stable "${idField}" id; a row is missing it.`,
      );
    }
    const id = String(rawId);
    if (seen.has(id)) {
      return unsupported(
        `Manual row ordering needs a unique "${idField}" per row; the id "${id}" is repeated.`,
      );
    }
    seen.add(id);
    rowTargets.push({ id, label: rowLabel(row, idField, id) });
  }

  return { rowTargets, support: { supported: true } };
}
